import { Preferences } from '@capacitor/preferences';
import { isNative } from '@/domains/shared/utils/native';
import {
  DEFAULT_THEME_PREFERENCE,
  THEME_PREFERENCE_KEY,
  type ThemePreference,
} from './constants';

/**
 * Fired on `window` by `useThemePreference` whenever the preference changes, so
 * app-level listeners react within the same tab. The DOM `storage` event only
 * fires in *other* tabs (and never on native, where the value lives in Capacitor
 * Preferences rather than localStorage), so it cannot be relied on for this.
 */
export const THEME_PREFERENCE_CHANGE_EVENT = 'theme-preference-change';

const isThemePreference = (value: string | null): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

/**
 * Applies the resolved theme to the document root.
 *
 * The `enabled` flag is the single gate for the whole theming feature (wired to
 * the QR-triggered settings toggle in `_app`). When it is `false` the app must
 * look exactly as it did before theming existed, so we force the `.light` class
 * on — this wins over `prefers-color-scheme`, so a system-dark device never
 * diverges from classic light mode. When `true`, `light`/`dark` force the theme
 * and `system` removes both classes so the `prefers-color-scheme` media query
 * in globals.css takes over.
 */
export function applyThemeClass(preference: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  root.classList.toggle('dark', preference === 'dark');
  root.classList.toggle('light', preference === 'light');
}

/**
 * Reads the persisted preference from the correct store for the platform
 * (Capacitor Preferences on native, localStorage on web). Falls back to the
 * default on any failure or unrecognised value.
 */
export async function loadThemePreference(): Promise<ThemePreference> {
  if (typeof window === 'undefined') return DEFAULT_THEME_PREFERENCE;
  if (isNative()) {
    try {
      const { value } = await Preferences.get({ key: THEME_PREFERENCE_KEY });
      return isThemePreference(value) ? value : DEFAULT_THEME_PREFERENCE;
    } catch {
      return DEFAULT_THEME_PREFERENCE;
    }
  }
  const stored = window.localStorage.getItem(THEME_PREFERENCE_KEY);
  return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
}
